/**
 * Task-Specific Actions
 * Actions defined specifically for the tasks entity
 * Strongly typed to ContactTaskRow row type
 * Uses shared builders to eliminate duplication
 */

import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import { ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import {
  getTaskDirectContactContextId,
  isTaskLinkedToCommunications,
} from "@/lib/utils/crm/task-linked-entity";
import {
  universalDeleteAction,
  universalEditAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalMakeTicketAction,
  universalLogTimeEntryAction,
  universalViewCompanyAction,
  universalViewDetailsAction,
  universalConfigureAutomatedTasksAction,
} from "@/lib/actions/definitions/universal-actions";
import {
  createStatusAction,
  createDuplicateTaskAction,
  createQuickCompleteAction,
  createCompleteTaskAction,
  createAddTaskNoteAction,
  createSetDueDateAction,
  createAssignTaskToMeAction,
} from "@/lib/actions/definitions/shared";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

type TaskLogActivityContext = ContactTaskRow & {
  effectiveLogActivityContactId?: string;
  communicationContactLoading?: boolean;
};

function createTaskLogActivityAction(): DetailActionStrict<ContactTaskRow> {
  const base = universalLogActivityAction<ContactTaskRow>();
  return {
    ...base,
    visible: (task) => {
      const t = task as TaskLogActivityContext;
      if (t.effectiveLogActivityContactId) return true;
      if (getTaskDirectContactContextId(t)) return true;
      if (isTaskLinkedToCommunications(t.entity_type) && t.entity_id) return true;
      return false;
    },
    disabled: (task) => {
      const t = task as TaskLogActivityContext;
      if (t.communicationContactLoading) return true;
      if (isTaskLinkedToCommunications(t.entity_type) && !t.effectiveLogActivityContactId) {
        return true;
      }
      return false;
    },
  };
}

/**
 * Complete task actions array
 * Combines universal and task-specific actions in the correct order
 * Order matches the original EnhancedTaskDetailConfig.ts for behavior preservation
 */
export const taskDetailActions: DetailActionStrict<ContactTaskRow>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<ContactTaskRow>(),

  // PRIMARY ACTIONS (Header)
  createStatusAction<ContactTaskRow>(),
  universalEditAction<ContactTaskRow>(),
  createDuplicateTaskAction<ContactTaskRow>(),
  createAssignTaskToMeAction<ContactTaskRow>(),
  universalSendEmailAction<ContactTaskRow>(),
  // Template-prefilled text — opens the send-text modal with a task template,
  // pre-seeding the task's linked contact when one exists.
  {
    id: "send-text-template",
    label: "Text from template",
    icon: "message-square",
    variant: "outline",
    action: "sendTextTemplate",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    bulkable: false,
    tooltip: "Send a templated text message — pick the contact and a ready-made message.",
  },
  createTaskLogActivityAction(),
  createAddTaskNoteAction<ContactTaskRow>(),
  createSetDueDateAction<ContactTaskRow>(),

  // TABLE ONLY ACTIONS
  universalViewDetailsAction<ContactTaskRow>("viewTask"),
  createQuickCompleteAction<ContactTaskRow>(),

  // UNIVERSAL ACTIONS (Both Contexts)
  universalMakeTicketAction<ContactTaskRow>(),
  universalLogTimeEntryAction<ContactTaskRow>(),
  universalViewCompanyAction<ContactTaskRow>(),
  universalConfigureAutomatedTasksAction<ContactTaskRow>(),
  createSendToSlackAction<ContactTaskRow>(),
  createSendToSalesChannelAction<ContactTaskRow>(),
  createOpenSlackChannelAction<ContactTaskRow>(),
  createCompleteTaskAction<ContactTaskRow>(),
  universalDeleteAction<ContactTaskRow>(),
];
